import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Chip,
} from "@mui/material";
import { Link } from "react-router-dom";
import {
  ArrowBack as ArrowBackIcon,
  LocalGasStation as LocalGasStationIcon,
  LocalCarWash as LocalCarWashIcon,
  CreditCard as CreditCardIcon,
} from "@mui/icons-material";
import { API_URL } from "../../utils/api-config";
import FuelCardList from "./FuelCardList";
import FuelCardForm from "./FuelCardForm";
import FuelCardDetails from "./FuelCardDetails";
import FuelCardAssignDriver from "./FuelCardAssignDriver";
import FuelCardAssignVehicle from "./FuelCardAssignVehicle";

// Interface pour les cartes
interface FuelCard {
  _id: string;
  cardNumber: string;
  provider: string;
  type: string;
  category: 'carburant' | 'lavage';
  expiryDate: string;
  isActive: boolean;
  isConnected: boolean;
  externalId?: string;
  assignedTo?: string;
  vehiclePlate?: string;
}

export default function FuelCardsPage() {
  const [tab, setTab] = useState(0);
  const [selectedCardNumber, setSelectedCardNumber] = useState<string | null>(null);
  const [allCards, setAllCards] = useState<FuelCard[]>([]);
  const [loading, setLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'carburant' | 'lavage'>('all');

  // Charger la liste des cartes
  useEffect(() => {
    const fetchCards = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/fuel-cards`, {
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        });
        const data = await res.json();
        // S'assurer que data est un tableau
        if (Array.isArray(data)) {
          setAllCards(data);
        } else if (data && Array.isArray(data.cards)) {
          setAllCards(data.cards);
        } else if (data && Array.isArray(data.data)) {
          setAllCards(data.data);
        } else {
          setAllCards([]);
        }
      } catch (err) {
        console.error("Erreur lors du chargement des cartes:", err);
        setAllCards([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCards();
  }, []);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const handleCardSelect = (event: { target: { value: string } }) => {
    setSelectedCardNumber(event.target.value);
  };

  const handleCategoryChange = (
    _event: React.MouseEvent<HTMLElement>,
    newCategory: 'all' | 'carburant' | 'lavage' | null,
  ) => {
    if (newCategory !== null) {
      setCategoryFilter(newCategory);
    }
  };

  // Filtrer les cartes par catégorie (avec protection)
  const safeCards = Array.isArray(allCards) ? allCards : [];
  const filteredCards = safeCards.filter(card => {
    if (categoryFilter === 'all') return true;
    return card.category === categoryFilter;
  });

  // Compter les cartes par catégorie
  const carburantCount = safeCards.filter(c => c.category === 'carburant' || !c.category).length;
  const lavageCount = safeCards.filter(c => c.category === 'lavage').length;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        bgcolor: "background.default",
        py: { xs: 2, md: 4 },
        px: { xs: 1, md: 2 },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 1200,
          mx: "auto",
          boxShadow: { xs: 0, sm: 3 },
          borderRadius: { xs: 0, sm: 4 },
          bgcolor: "background.paper",
          p: { xs: 2, sm: 3, md: 4 },
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
            mb: 3,
            width: "100%",
          }}
        >
          <Button
            component={Link}
            to="/"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            color="primary"
            sx={{ borderRadius: 2, mb: { xs: 1, sm: 0 } }}
          >
            Tableau de bord
          </Button>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ fontSize: { xs: "1.2rem", sm: "1.5rem" } }}
          >
            <CreditCardIcon sx={{ mr: 1, verticalAlign: "middle" }} />
            Gestion des cartes
          </Typography>
        </Box>

        {/* Filtres par catégorie */}
        <Paper sx={{ mb: 3, p: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" color="text.secondary">
              Catégorie :
            </Typography>
            <ToggleButtonGroup
              value={categoryFilter}
              exclusive
              onChange={handleCategoryChange}
              size="small"
            >
              <ToggleButton value="all">
                Toutes
                <Chip label={safeCards.length} size="small" sx={{ ml: 1 }} />
              </ToggleButton>
              <ToggleButton value="carburant" sx={{ color: categoryFilter === 'carburant' ? 'primary.main' : 'inherit' }}>
                <Box component="img" src="/carburant-icon.png" sx={{ width: 20, height: 20, mr: 0.5 }} />
                Carburant
                <Chip label={carburantCount} size="small" sx={{ ml: 1 }} color="primary" variant="outlined" />
              </ToggleButton>
              <ToggleButton value="lavage" sx={{ color: categoryFilter === 'lavage' ? 'info.main' : 'inherit' }}>
                <Box component="img" src="/lavage-icon.png" sx={{ width: 20, height: 20, mr: 0.5 }} />
                Lavage
                <Chip label={lavageCount} size="small" sx={{ ml: 1 }} color="info" variant="outlined" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Paper>

        <Paper
          sx={{ mb: 3, borderRadius: 2, overflow: "hidden", width: "100%" }}
        >
          <Tabs
            value={tab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              bgcolor: "background.paper",
              "& .MuiTabs-indicator": { height: 3 },
              "& .MuiTab-root": { py: 2, fontWeight: 600 },
            }}
          >
            <Tab label="Liste des cartes" />
            <Tab label="Ajouter une carte" />
            <Tab label="Détail carte" />
            <Tab label="Associer chauffeur" />
            <Tab label="Associer véhicule" />
          </Tabs>
        </Paper>

        <Box sx={{ flex: 1, width: "100%", overflow: "auto" }}>
          {tab === 0 && (
            <>
              <Typography
                sx={{ mb: 2, fontStyle: "italic", color: "text.secondary" }}
              >
                Cliquez sur une carte pour voir ses détails
              </Typography>
              <FuelCardList
                categoryFilter={categoryFilter}
                onSelectCard={(card) => {
                  setSelectedCardNumber(card.cardNumber);
                  setTab(2);
                }}
              />
            </>
          )}
          {tab === 1 && <FuelCardForm />}
          {tab === 2 && (
            <>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="card-select-label">
                  Sélectionner une carte
                </InputLabel>
                <Select
                  labelId="card-select-label"
                  id="card-select"
                  value={selectedCardNumber || ""}
                  label="Sélectionner une carte"
                  onChange={handleCardSelect}
                  disabled={loading}
                >
                  {filteredCards.map((card) => (
                    <MenuItem key={card.cardNumber} value={card.cardNumber}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {card.category === 'lavage' ? (
                          <Box component="img" src="/lavage-icon.png" sx={{ width: 20, height: 20 }} />
                        ) : (
                          <Box component="img" src="/carburant-icon.png" sx={{ width: 20, height: 20 }} />
                        )}
                        {card.cardNumber} - {card.provider} ({card.type})
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedCardNumber ? (
                <FuelCardDetails cardNumber={selectedCardNumber} />
              ) : (
                <Typography sx={{ textAlign: "center", mt: 4 }}>
                  Sélectionnez une carte dans le menu déroulant pour voir ses détails.
                </Typography>
              )}
            </>
          )}
          {tab === 3 && <FuelCardAssignDriver />}
          {tab === 4 && <FuelCardAssignVehicle />}
        </Box>
      </Box>
    </Box>
  );
}
